export type JobsDashboardValue = unknown;

export type JobsDashboardRecord = Record<string, unknown>;

export enum JobsDashboardStatus {
    Pending = "pending",
    Running = "running",
    Finished = "finished",
    Failed = "failed",
}

export enum JobsDashboardScheduleType {
    Hourly = "hourly",
    Daily = "daily",
    Monthly = "monthly",
}

export const JOB_STATUS_VALUES = Object.values(JobsDashboardStatus);

export const JOB_SCHEDULE_TYPE_VALUES = Object.values(JobsDashboardScheduleType);

type ResultPayload = {
    message?: JobsDashboardValue;
    data?: JobsDashboardRecord;
};

export class JobsDashboardUtils {
    public static ROLES = {
        ROOT: "jobs-dashboard",
        STATUS: "jobs-status",
        REFRESH: "jobs-refresh",
        RUN_ALL: "jobs-run-all",
        LIST: "jobs-list",
        LATEST_RESULT: "jobs-latest-result",
        EDIT_BUTTON: "jobs-edit-button",
        RUN_BUTTON: "jobs-run-button",
        ABORT_BUTTON: "jobs-abort-button",
        SAVE_BUTTON: "jobs-save-button",
        RESULT_BUTTON: "jobs-result-button",
        HISTORY_PANEL: "jobs-history-panel",
        HISTORY_CARD: "jobs-history-card",
        HISTORY_TITLE: "jobs-history-title",
        HISTORY_LIST: "jobs-history-list",
        HISTORY_CLOSE: "jobs-history-close",
    } as const;

    public static toText(value: JobsDashboardValue): string {
        if (value === null || value === undefined) {
            return "";
        }
        return String(value);
    }

    public static normalizeJobKey(value: JobsDashboardValue): string {
        return this.toText(value).trim();
    }

    public static toBool(value: JobsDashboardValue): boolean {
        return value === true || value === 1 || value === "1";
    }

    public static summarizeResult(payload: ResultPayload): string {
        const message = this.toText(payload?.message || "").trim();
        const exitCode =
            payload?.data && Object.prototype.hasOwnProperty.call(payload.data, "exit_code")
                ? payload.data.exit_code
                : null;
        const dryRun =
            payload?.data && Object.prototype.hasOwnProperty.call(payload.data, "dry_run")
                ? this.toBool(payload.data.dry_run)
                : false;

        if (dryRun) {
            return "Dry run passed";
        }

        if (exitCode !== null && exitCode !== undefined && this.toText(exitCode) !== "") {
            return `Exit ${this.toText(exitCode)}`;
        }

        return message || "No result";
    }

    public static nowIso(date = new Date()): string {
        return date.toISOString();
    }

    public static normalizeStatus(value: JobsDashboardValue): JobsDashboardStatus {
        const normalized = this.toText(value).trim().toLowerCase();
        return JOB_STATUS_VALUES.find((item) => item === normalized) ?? JobsDashboardStatus.Pending;
    }

    public static normalizeScheduleType(value: JobsDashboardValue): JobsDashboardScheduleType {
        const normalized = this.toText(value).trim().toLowerCase();
        return JOB_SCHEDULE_TYPE_VALUES.find((item) => item === normalized) ?? JobsDashboardScheduleType.Daily;
    }

    public static sortJobsBySchedule(jobs: JobsDashboardRecord[]): JobsDashboardRecord[] {
        return [...jobs].sort((left, right) => this.compareJobsBySchedule(left, right));
    }

    private static compareJobsBySchedule(left: JobsDashboardRecord, right: JobsDashboardRecord): number {
        const leftSchedule = this.toScheduleSortKey(left);
        const rightSchedule = this.toScheduleSortKey(right);

        if (leftSchedule.timeMinutes !== rightSchedule.timeMinutes) {
            return leftSchedule.timeMinutes - rightSchedule.timeMinutes;
        }

        if (leftSchedule.dayOfMonth !== rightSchedule.dayOfMonth) {
            return leftSchedule.dayOfMonth - rightSchedule.dayOfMonth;
        }

        if (leftSchedule.typeOrder !== rightSchedule.typeOrder) {
            return leftSchedule.typeOrder - rightSchedule.typeOrder;
        }

        return leftSchedule.jobKey.localeCompare(rightSchedule.jobKey);
    }

    private static toScheduleSortKey(job: JobsDashboardRecord): {
        timeMinutes: number;
        dayOfMonth: number;
        typeOrder: number;
        jobKey: string;
    } {
        const scheduleType = this.normalizeScheduleType(job.schedule_type);
        const scheduleTime = this.parseScheduleTime(job.schedule_time);
        const scheduleDay = this.parseScheduleDay(job.schedule_day);

        return {
            timeMinutes: scheduleTime,
            dayOfMonth: scheduleType === JobsDashboardScheduleType.Monthly ? scheduleDay : 0,
            typeOrder: this.scheduleTypeOrder(scheduleType),
            jobKey: this.normalizeJobKey(job.job_key),
        };
    }

    private static parseScheduleTime(value: JobsDashboardValue): number {
        const text = this.toText(value).trim();
        if (!/^\d{2}:\d{2}$/.test(text)) {
            return -1;
        }

        const [hoursRaw, minutesRaw] = text.split(":");
        const hours = Number.parseInt(hoursRaw, 10);
        const minutes = Number.parseInt(minutesRaw, 10);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return -1;
        }

        return hours * 60 + minutes;
    }

    private static parseScheduleDay(value: JobsDashboardValue): number {
        const numeric = Number.parseInt(this.toText(value).trim(), 10);
        return Number.isNaN(numeric) ? 0 : numeric;
    }

    private static scheduleTypeOrder(value: JobsDashboardScheduleType): number {
        switch (value) {
            case JobsDashboardScheduleType.Hourly:
                return 0;
            case JobsDashboardScheduleType.Daily:
                return 1;
            case JobsDashboardScheduleType.Monthly:
                return 2;
            default:
                return 9;
        }
    }
}
